import type { ComponentType } from "react"
import {
  ActivityIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  CalendarDaysIcon,
  FileTextIcon,
  PackageIcon,
  ScaleIcon,
  TrendingUpIcon,
  UsersIcon,
} from "lucide-react"

import { formatOverviewCardDisplayValue } from "@/features/finance/lib/finance-overview-integration"
import { FinanceOverviewCardId } from "@/features/finance/model/finance-normalized-overview"
import type { OverviewCard } from "@ibada/shared-types/overview"

import type { KpiCardItem } from "@ibada/ui"
import { KpiIntent } from "@ibada/ui"

/** Prefer one row of high-signal tiles; remaining API cards are ignored once `MAX_KPIS` is reached. */
const DASHBOARD_KPI_CARD_ORDER: string[] = [
  FinanceOverviewCardId.NetBalance,
  FinanceOverviewCardId.TotalIncome,
  FinanceOverviewCardId.TotalExpenses,
  "membersTotal",
  "totalMembers",
  "dashboardMembersTotal",
  FinanceOverviewCardId.ActiveActivitiesCount,
  "activeMembers",
  "pendingApprovalsCount",
  "pendingApprovalCount",
  "newMembersPeriod",
  "newMembers",
  FinanceOverviewCardId.TransactionCount,
  "visitors",
  "dashboardAssetsTotal",
  "assetsTotal",
  "dashboardFinanceNetBalance",
  "activitiesUpcoming",
  "activitiesActive",
]

const MAX_KPIS = 5

function coerceAbsOverviewValue(card: OverviewCard): number {
  const { value } = card
  if (typeof value === "number") return Number.isFinite(value) ? Math.abs(value) : 1
  if (typeof value === "string") {
    const n = Number(value)
    return Number.isFinite(n) ? Math.abs(n) : 1
  }
  return 1
}

function pickIcon(cardId: string): ComponentType<{ className?: string }> {
  const id = cardId.toLowerCase()
  if (id.includes("member") || id.includes("people")) return UsersIcon
  if (id.includes("income") || id.includes("revenue")) return ArrowUpIcon
  if (id.includes("expense")) return ArrowDownIcon
  if (id.includes("net") || id.includes("balance")) return ScaleIcon
  if (id.includes("asset")) return PackageIcon
  if (id.includes("activit")) return CalendarDaysIcon
  if (id.includes("upcoming")) return ActivityIcon
  if (id.includes("pending")) return TrendingUpIcon
  return FileTextIcon
}

function pickIntent(cardId: string, numeric: number): KpiIntent {
  if (
    cardId === FinanceOverviewCardId.TotalIncome ||
    cardId === "activitiesActive" ||
    cardId.includes("growth")
  ) {
    return KpiIntent.Success
  }
  if (cardId === FinanceOverviewCardId.TotalExpenses) return KpiIntent.Danger
  if (
    cardId === FinanceOverviewCardId.NetBalance ||
    cardId === "dashboardFinanceNetBalance" ||
    cardId.includes("Net")
  ) {
    return numeric >= 0 ? KpiIntent.Info : KpiIntent.Danger
  }
  return KpiIntent.Neutral
}

export type BuildDashboardKpisOptions = {
  locale: string
  currencyCode: string | null
  formatPercentageLabel: (percentage: number) => string
  translateTitleKey: (key: string | undefined | null) => string
  translateDescriptionKey?: (key: string | undefined | null) => string
}

export function buildDashboardKpiCardItems(
  cards: OverviewCard[],
  options: BuildDashboardKpisOptions,
): KpiCardItem[] {
  const translateDescription = options.translateDescriptionKey ?? (() => "")

  const byId = new Map(cards.map((c) => [c.id, c]))
  const orderedIds: string[] = []
  for (const candidate of DASHBOARD_KPI_CARD_ORDER) {
    if (byId.has(candidate)) orderedIds.push(candidate)
  }
  const remaining = cards
    .map((c) => c.id)
    .filter((id) => !orderedIds.includes(id))
    .sort((a, b) => a.localeCompare(b))

  const selectedIds = [...orderedIds, ...remaining].slice(0, MAX_KPIS)

  const selectedCards = selectedIds.map((id) => byId.get(id)).filter((c): c is OverviewCard => Boolean(c))

  const numerics = selectedCards.map((c) => coerceAbsOverviewValue(c))

  const baseline = Math.max(1, ...numerics)

  return selectedCards.map((card, index) => {
    const slug = numerics[index] ?? 1
    const pct = Math.round((slug / baseline) * 100)
    const displayValue = formatOverviewCardDisplayValue(
      card.value,
      card.valueFormat,
      options.currencyCode ?? "USD",
      options.locale,
    )
    const label = options.translateTitleKey(card.titleKey)

    let numericForIntent = 0
    if (typeof card.value === "number") numericForIntent = card.value
    else if (typeof card.value === "string") {
      const parsed = Number(card.value)
      numericForIntent = Number.isFinite(parsed) ? parsed : 0
    }

    const helperText =
      typeof card.descriptionKey === "string" && card.descriptionKey.trim().length > 0 ?
        translateDescription(card.descriptionKey)
      : ""

    return {
      key: card.id,
      icon: pickIcon(card.id),
      intent: pickIntent(card.id, numericForIntent),
      label,
      value: displayValue,
      helperText,
      percentage: pct,
      percentageLabel: options.formatPercentageLabel(pct),
    }
  })
}
